import { useEffect, useMemo, useState } from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";
import {
  Activity,
  BarChart3,
  Bell,
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  Columns3,
  Contrast,
  FileText,
  Flag,
  GitPullRequest,
  KanbanSquare,
  ListTodo,
  LogOut,
  Menu,
  Moon,
  Palette,
  Plus,
  Rocket,
  Search,
  Settings,
  SlidersHorizontal,
  SunMedium,
  User,
  Workflow
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { create } from "zustand";
import clsx from "clsx";
import type {
  AutomationRule,
  BoardPayload,
  CustomFieldDefinition,
  Issue,
  Project,
  Sprint,
  WorkflowStatus
} from "@pms/shared";
import { api, getAccessToken, setAccessToken, type AuthState } from "./api";

type ThemeId = "evergreen" | "graphite" | "indigo" | "sunrise";
type Density = "comfortable" | "compact";
type SidebarStyle = "solid" | "glass";

const themeOptions: Array<{ id: ThemeId; name: string; accent: string; icon: typeof Palette }> = [
  { id: "evergreen", name: "Evergreen", accent: "#218c74", icon: Palette },
  { id: "graphite", name: "Graphite", accent: "#7c8cff", icon: Moon },
  { id: "indigo", name: "Indigo", accent: "#5b6ee1", icon: Contrast },
  { id: "sunrise", name: "Sunrise", accent: "#d86c35", icon: SunMedium }
];

interface UiStore {
  sidebarCollapsed: boolean;
  theme: ThemeId;
  density: Density;
  sidebarStyle: SidebarStyle;
  toggleSidebar: () => void;
  setTheme: (theme: ThemeId) => void;
  setDensity: (density: Density) => void;
  setSidebarStyle: (style: SidebarStyle) => void;
}

const useUiStore = create<UiStore>((set) => ({
  sidebarCollapsed: false,
  theme: readUiPreference<ThemeId>("pms-theme", "evergreen"),
  density: readUiPreference<Density>("pms-density", "comfortable"),
  sidebarStyle: readUiPreference<SidebarStyle>("pms-sidebar", "solid"),
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setTheme: (theme) => {
    localStorage.setItem("pms-theme", theme);
    set({ theme });
  },
  setDensity: (density) => {
    localStorage.setItem("pms-density", density);
    set({ density });
  },
  setSidebarStyle: (sidebarStyle) => {
    localStorage.setItem("pms-sidebar", sidebarStyle);
    set({ sidebarStyle });
  }
}));

export default function App() {
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [checking, setChecking] = useState(Boolean(getAccessToken()));
  const theme = useUiStore((state) => state.theme);
  const density = useUiStore((state) => state.density);
  const sidebarStyle = useUiStore((state) => state.sidebarStyle);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.density = density;
    document.documentElement.dataset.sidebar = sidebarStyle;
    document.documentElement.style.colorScheme = theme === "graphite" ? "dark" : "light";
  }, [theme, density, sidebarStyle]);

  useEffect(() => {
    if (!getAccessToken()) return;
    api
      .me()
      .then(({ user }) => setAuth({ accessToken: getAccessToken(), user }))
      .catch(() => api.refresh().then(setAuth).catch(() => setAccessToken("")))
      .finally(() => setChecking(false));
  }, []);

  if (checking) return <FullScreenStatus label="Loading" />;
  if (!auth) return <Login onAuth={setAuth} />;

  return (
    <Routes>
      <Route path="/" element={<Dashboard auth={auth} onLogout={() => setAuth(null)} />} />
      <Route path="/projects/:key/*" element={<ProjectShell auth={auth} onLogout={() => setAuth(null)} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Login({ onAuth }: { onAuth: (auth: AuthState) => void }) {
  const [email, setEmail] = useState("admin@tasks.expandium.net");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const mutation = useMutation({
    mutationFn: () => api.login(email, password),
    onSuccess: onAuth,
    onError: (err) => setError(err.message)
  });

  return (
    <main className="loginShell">
      <section className="loginPanel">
        <div className="brandRow">
          <div className="brandMark"><KanbanSquare size={22} /></div>
          <div>
            <h1>Expandium Tasks</h1>
            <p>Self-hosted project management</p>
          </div>
        </div>
        <form
          className="formStack"
          onSubmit={(event) => {
            event.preventDefault();
            setError("");
            mutation.mutate();
          }}
        >
          <label>
            Email
            <input value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" />
          </label>
          <label>
            Password
            <input
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              autoComplete="current-password"
            />
          </label>
          {error ? <div className="errorBox">{error}</div> : null}
          <button className="primaryButton" disabled={mutation.isPending}>
            {mutation.isPending ? "Signing in" : "Sign in"}
          </button>
        </form>
      </section>
    </main>
  );
}

function Dashboard({ auth, onLogout }: { auth: AuthState; onLogout: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const [createOpen, setCreateOpen] = useState(false);
  const [projectForm, setProjectForm] = useState({ key: "", name: "", mode: "scrum" as Project["mode"] });
  const createProject = useMutation({
    mutationFn: () => api.createProject(projectForm),
    onSuccess: ({ project }) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setCreateOpen(false);
      navigate(`/projects/${project.key}/board`);
    }
  });

  return (
    <div className="appFrame">
      <aside className="globalSidebar">
        <Link className="appLogo" to="/">
          <KanbanSquare />
          <span>Tasks</span>
        </Link>
        <div className="sidebarFooter">
          <span className="userPill"><User size={14} /> {auth.user.name}</span>
          <ThemeMenu />
          <button
            className="iconButton"
            title="Sign out"
            onClick={() => api.logout().finally(onLogout)}
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>
      <main className="mainPane">
        <header className="pageHeader">
          <div>
            <span className="eyebrow">Workspace</span>
            <h1>Projects</h1>
            <p>{projects.data?.projects.length ?? 0} workspaces</p>
          </div>
          <button className="primaryButton compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} /> Project
          </button>
        </header>
        {projects.isLoading ? <InlineStatus label="Loading projects" /> : null}
        <div className="projectGrid">
          {projects.data?.projects.map((project) => (
            <Link key={project.id} className="projectTile" to={`/projects/${project.key}/board`}>
              <div className="tileTop">
                <div className="tileKey">{project.key}</div>
                <span className="modePill">{project.mode}</span>
              </div>
              <h2>{project.name}</h2>
              <p>{project.description ?? "No description"}</p>
              <span className="tileRole">{project.role}</span>
            </Link>
          ))}
        </div>
      </main>
      <Dialog.Root open={createOpen} onOpenChange={setCreateOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="dialogOverlay" />
          <Dialog.Content className="dialogCard smallDialog">
            <Dialog.Title>New Project</Dialog.Title>
            <form
              className="formStack"
              onSubmit={(event) => {
                event.preventDefault();
                createProject.mutate();
              }}
            >
              <label>Key<input value={projectForm.key} onChange={(event) => setProjectForm({ ...projectForm, key: event.target.value })} /></label>
              <label>Name<input value={projectForm.name} onChange={(event) => setProjectForm({ ...projectForm, name: event.target.value })} /></label>
              <label>Mode<select value={projectForm.mode} onChange={(event) => setProjectForm({ ...projectForm, mode: event.target.value as Project["mode"] })}><option value="scrum">Scrum</option><option value="kanban">Kanban</option></select></label>
              <button className="primaryButton" disabled={createProject.isPending}>Create</button>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function ProjectShell({ auth, onLogout }: { auth: AuthState; onLogout: () => void }) {
  const { key = "" } = useParams();
  const collapsed = useUiStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);
  const project = useQuery({ queryKey: ["project", key], queryFn: () => api.project(key), enabled: Boolean(key) });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!key || !getAccessToken()) return;
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const socket = new WebSocket(`${protocol}://${window.location.host}/api/ws?project=${key}&token=${encodeURIComponent(getAccessToken())}`);
    socket.onmessage = (event) => {
      if (event.data === "pong") return;
      queryClient.invalidateQueries({ queryKey: ["board", key] });
      queryClient.invalidateQueries({ queryKey: ["issues", key] });
      queryClient.invalidateQueries({ queryKey: ["sprints", key] });
      queryClient.invalidateQueries({ queryKey: ["roadmap", key] });
      queryClient.invalidateQueries({ queryKey: ["reports", key] });
    };
    const interval = window.setInterval(() => socket.readyState === socket.OPEN && socket.send("ping"), 25_000);
    return () => {
      window.clearInterval(interval);
      socket.close();
    };
  }, [key, queryClient]);

  if (project.isLoading) return <FullScreenStatus label="Loading project" />;
  if (!project.data) return <FullScreenStatus label="Project unavailable" />;

  const nav = [
    { to: "board", label: "Board", icon: Columns3 },
    { to: "backlog", label: "Backlog", icon: ListTodo },
    { to: "roadmap", label: "Roadmap", icon: CalendarClock },
    { to: "reports", label: "Reports", icon: BarChart3 },
    { to: "settings", label: "Settings", icon: Settings }
  ];

  return (
    <div className={clsx("appFrame", collapsed && "collapsed")}>
      <aside className="globalSidebar">
        <Link className="appLogo" to="/">
          <KanbanSquare />
          <span>Tasks</span>
        </Link>
        <button className="sidebarToggle" onClick={toggleSidebar} title="Toggle sidebar">
          {collapsed ? <Menu size={18} /> : <ChevronLeft size={18} />}
        </button>
        <nav className="sideNav">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={`/projects/${key}/${item.to}`}
              className={({ isActive }) => clsx("navItem", isActive && "active")}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebarFooter">
          <span className="userPill"><User size={14} /> {auth.user.name}</span>
          <ThemeMenu />
          <button className="iconButton" title="Sign out" onClick={() => api.logout().finally(onLogout)}>
            <LogOut size={18} />
          </button>
        </div>
      </aside>
      <main className="mainPane">
        <header className="projectHeader">
          <div className="projectIdentity">
            <div className="projectBadge">{project.data.project.key}</div>
            <div>
              <span className="eyebrow">{project.data.project.mode}</span>
              <h1>{project.data.project.name}</h1>
              <p>{project.data.project.role}</p>
            </div>
          </div>
          <div className="statusRibbon">
            {project.data.statuses.map((status) => (
              <span key={status.id} style={{ borderColor: status.color }}>
                <span className="statusDot" style={{ background: status.color }} />
                {status.name}
              </span>
            ))}
          </div>
        </header>
        <Routes>
          <Route index element={<Navigate to="board" replace />} />
          <Route path="board" element={<BoardView projectKey={key} />} />
          <Route path="backlog" element={<BacklogView projectKey={key} statuses={project.data.statuses} />} />
          <Route path="roadmap" element={<RoadmapView projectKey={key} />} />
          <Route path="reports" element={<ReportsView projectKey={key} />} />
          <Route path="settings" element={<SettingsView projectKey={key} />} />
        </Routes>
      </main>
    </div>
  );
}

function BoardView({ projectKey }: { projectKey: string }) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [queryText, setQueryText] = useState("");
  const board = useQuery({ queryKey: ["board", projectKey], queryFn: () => api.board(projectKey) });
  const createIssue = useMutation({
    mutationFn: (payload: Partial<Issue>) => api.createIssue(projectKey, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["board", projectKey] })
  });
  const updateIssue = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Issue> }) => api.updateIssue(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["board", projectKey] })
  });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const openIssue = params.get("issue");
  const data = board.data;
  const filtered = useMemo(() => {
    const issues = data?.issues ?? [];
    if (!queryText.trim()) return issues;
    const q = queryText.toLowerCase();
    return issues.filter((issue) => [issue.key, issue.title, issue.priority, issue.type, issue.assignee?.name ?? ""].join(" ").toLowerCase().includes(q));
  }, [data?.issues, queryText]);

  function onDragEnd(event: DragEndEvent) {
    const issueId = String(event.active.id);
    const over = event.over;
    if (!over || !data) return;
    const overType = over.data.current?.type;
    const targetStatusId =
      overType === "status"
        ? String(over.id)
        : data.issues.find((issue) => issue.id === over.id)?.statusId;
    if (!targetStatusId) return;
    const issue = data.issues.find((item) => item.id === issueId);
    if (!issue || issue.statusId === targetStatusId) return;
    updateIssue.mutate({ id: issue.id, payload: { statusId: targetStatusId, rank: rankNow() } });
  }

  if (board.isLoading) return <InlineStatus label="Loading board" />;
  if (!data) return null;

  return (
    <section className="workSurface">
      <div className="surfaceToolbar">
        <div className="toolbarMain">
          <div className="searchBox"><Search size={16} /><input value={queryText} onChange={(event) => setQueryText(event.target.value)} placeholder="Search issues" /></div>
          <div className="surfaceStats">
            <span>{filtered.length} issues</span>
            <span>{data.activeSprint?.name ?? "Kanban"}</span>
          </div>
        </div>
        <div className="toolbarActions">
          <ThemeMenu />
          <NewIssueButton statuses={data.statuses} users={data.users} onCreate={(payload) => createIssue.mutate(payload)} />
        </div>
      </div>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="boardGrid" style={{ gridTemplateColumns: `repeat(${data.statuses.length}, minmax(260px, 1fr))` }}>
          {data.statuses.map((status) => {
            const columnIssues = filtered.filter((issue) => issue.statusId === status.id);
            return <BoardColumn key={status.id} status={status} issues={columnIssues} onOpen={(issue) => setParams({ issue: issue.key })} />;
          })}
        </div>
      </DndContext>
      {openIssue ? <IssueDrawer issueId={openIssue} projectKey={projectKey} onClose={() => setParams({})} board={data} /> : null}
    </section>
  );
}

function BoardColumn({ status, issues, onOpen }: { status: WorkflowStatus; issues: Issue[]; onOpen: (issue: Issue) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: status.id, data: { type: "status" } });
  return (
    <section ref={setNodeRef} className={clsx("boardColumn", isOver && "dropTarget")}>
      <header>
        <span className="statusDot" style={{ background: status.color }} />
        <strong>{status.name}</strong>
        <span>{issues.length}</span>
      </header>
      <SortableContext items={issues.map((issue) => issue.id)} strategy={verticalListSortingStrategy}>
        <div className="cardStack">
          {issues.map((issue) => <IssueCard key={issue.id} issue={issue} onOpen={() => onOpen(issue)} />)}
        </div>
      </SortableContext>
    </section>
  );
}

function IssueCard({ issue, onOpen }: { issue: Issue; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: issue.id, data: { type: "issue" } });
  return (
    <article
      ref={setNodeRef}
      className={clsx("issueCard", isDragging && "dragging")}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      onClick={onOpen}
      {...attributes}
      {...listeners}
    >
      <div className="cardMeta"><span>{issue.key}</span><span className="typePill">{issue.type}</span></div>
      <h3>{issue.title}</h3>
      <div className="cardFooter">
        <span className={clsx("priority", `priority-${issue.priority}`)}><Flag size={13} /> {issue.priority}</span>
        <span className="pointsPill">{issue.storyPoints ?? "-"} pts</span>
      </div>
    </article>
  );
}

function NewIssueButton({
  statuses,
  users,
  onCreate
}: {
  statuses: WorkflowStatus[];
  users: BoardPayload["users"];
  onCreate: (payload: Partial<Issue>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<Issue>>({
    type: "task",
    priority: "medium",
    title: "",
    storyPoints: 1,
    labels: []
  });
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild><button className="primaryButton compact"><Plus size={16} /> Issue</button></Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="dialogOverlay" />
        <Dialog.Content className="dialogCard smallDialog">
          <Dialog.Title>New Issue</Dialog.Title>
          <form
            className="formGrid"
            onSubmit={(event) => {
              event.preventDefault();
              onCreate({ ...form, description: { type: "doc", text: String(form.descriptionText ?? "") }, labels: parseLabels(String((form.labels as unknown as string) ?? "")) });
              setOpen(false);
              setForm({ type: "task", priority: "medium", title: "", storyPoints: 1, labels: [] });
            }}
          >
            <label className="span2">Title<input required value={form.title ?? ""} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label>
            <label>Type<select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value as Issue["type"] })}><option value="task">Task</option><option value="bug">Bug</option><option value="story">Story</option><option value="epic">Epic</option><option value="subtask">Subtask</option></select></label>
            <label>Priority<select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value as Issue["priority"] })}><option value="lowest">Lowest</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="highest">Highest</option></select></label>
            <label>Status<select value={form.statusId ?? ""} onChange={(event) => setForm({ ...form, statusId: event.target.value })}><option value="">Default</option>{statuses.map((status) => <option key={status.id} value={status.id}>{status.name}</option>)}</select></label>
            <label>Assignee<select value={form.assigneeId ?? ""} onChange={(event) => setForm({ ...form, assigneeId: event.target.value || null })}><option value="">Unassigned</option>{users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}</select></label>
            <label>Points<input type="number" min={0} max={100} value={form.storyPoints ?? 0} onChange={(event) => setForm({ ...form, storyPoints: Number(event.target.value) })} /></label>
            <label>Due date<input type="date" value={form.dueDate ?? ""} onChange={(event) => setForm({ ...form, dueDate: event.target.value || null })} /></label>
            <label className="span2">Labels<input value={(form.labels as unknown as string) ?? ""} onChange={(event) => setForm({ ...form, labels: event.target.value as unknown as string[] })} /></label>
            <label className="span2">Description<textarea value={form.descriptionText ?? ""} onChange={(event) => setForm({ ...form, descriptionText: event.target.value })} /></label>
            <button className="primaryButton span2">Create issue</button>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function IssueDrawer({ issueId, projectKey, onClose, board }: { issueId: string; projectKey: string; onClose: () => void; board: BoardPayload }) {
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ["issue", issueId], queryFn: () => api.issue(issueId) });
  const update = useMutation({
    mutationFn: (payload: Partial<Issue>) => api.updateIssue(issueId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issue", issueId] });
      queryClient.invalidateQueries({ queryKey: ["board", projectKey] });
    }
  });
  const comment = useMutation({
    mutationFn: (body: string) => api.comment(issueId, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["issue", issueId] })
  });
  const [commentBody, setCommentBody] = useState("");
  const issue = detail.data?.issue;

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialogOverlay" />
        <Dialog.Content className="issueDrawer">
          <Dialog.Title>{issue ? `${issue.key} · ${issue.title}` : "Issue"}</Dialog.Title>
          {detail.isLoading ? <InlineStatus label="Loading issue" /> : null}
          {issue ? (
            <div className="drawerGrid">
              <section className="drawerMain">
                <label>Title<input value={issue.title} onChange={(event) => update.mutate({ title: event.target.value })} /></label>
                <label>Description<textarea defaultValue={issue.descriptionText ?? ""} onBlur={(event) => update.mutate({ description: { type: "doc", text: event.target.value } })} /></label>
                <h3>Comments</h3>
                <form
                  className="commentBox"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (!commentBody.trim()) return;
                    comment.mutate(commentBody);
                    setCommentBody("");
                  }}
                >
                  <textarea value={commentBody} onChange={(event) => setCommentBody(event.target.value)} />
                  <button className="primaryButton compact">Comment</button>
                </form>
                <div className="activityFeed">
                  {detail.data?.comments.map((item) => (
                    <article key={item.id}>
                      <strong>{item.author.name}</strong>
                      <p>{item.bodyText}</p>
                    </article>
                  ))}
                </div>
                <h3>Activity</h3>
                <div className="activityFeed">
                  {detail.data?.history.map((item) => (
                    <article key={item.id}>
                      <strong>{item.field}</strong>
                      <p>{new Date(item.createdAt).toLocaleString()}</p>
                    </article>
                  ))}
                </div>
              </section>
              <aside className="drawerSide">
                <label>Status<select value={issue.statusId} onChange={(event) => update.mutate({ statusId: event.target.value })}>{board.statuses.map((status) => <option key={status.id} value={status.id}>{status.name}</option>)}</select></label>
                <label>Assignee<select value={issue.assigneeId ?? ""} onChange={(event) => update.mutate({ assigneeId: event.target.value || null })}><option value="">Unassigned</option>{board.users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}</select></label>
                <label>Priority<select value={issue.priority} onChange={(event) => update.mutate({ priority: event.target.value as Issue["priority"] })}><option value="lowest">Lowest</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="highest">Highest</option></select></label>
                <label>Points<input type="number" min={0} max={100} value={issue.storyPoints ?? 0} onChange={(event) => update.mutate({ storyPoints: Number(event.target.value) })} /></label>
                <label>Start<input type="date" value={issue.startDate ?? ""} onChange={(event) => update.mutate({ startDate: event.target.value || null })} /></label>
                <label>Due<input type="date" value={issue.dueDate ?? ""} onChange={(event) => update.mutate({ dueDate: event.target.value || null })} /></label>
                <div className="devList">
                  <h3>Development</h3>
                  {detail.data?.development.map((item) => <p key={String(item.id)}><GitPullRequest size={14} /> {String(item.title)}</p>)}
                </div>
              </aside>
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function BacklogView({ projectKey, statuses }: { projectKey: string; statuses: WorkflowStatus[] }) {
  const queryClient = useQueryClient();
  const backlog = useQuery({ queryKey: ["issues", projectKey, "backlog"], queryFn: () => api.issues(projectKey, "?sprint=none&limit=200") });
  const sprints = useQuery({ queryKey: ["sprints", projectKey], queryFn: () => api.sprints(projectKey) });
  const [sprintName, setSprintName] = useState("");
  const createSprint = useMutation({
    mutationFn: () => api.createSprint(projectKey, { name: sprintName || `Sprint ${new Date().toLocaleDateString()}` }),
    onSuccess: () => {
      setSprintName("");
      queryClient.invalidateQueries({ queryKey: ["sprints", projectKey] });
    }
  });
  const moveIssue = useMutation({
    mutationFn: ({ issueId, sprintId }: { issueId: string; sprintId: string | null }) => api.updateIssue(issueId, { sprintId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issues", projectKey] });
      queryClient.invalidateQueries({ queryKey: ["sprints", projectKey] });
    }
  });
  const sprintAction = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "start" | "complete" }) =>
      action === "start" ? api.startSprint(projectKey, id) : api.completeSprint(projectKey, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sprints", projectKey] });
      queryClient.invalidateQueries({ queryKey: ["board", projectKey] });
    }
  });

  const futureSprint = sprints.data?.sprints.find((sprint) => sprint.state === "future");
  return (
    <section className="splitSurface">
      <div className="panel">
        <header className="panelHeader"><h2>Backlog</h2><span>{backlog.data?.issues.length ?? 0}</span></header>
        <div className="issueList">
          {backlog.data?.issues.map((issue) => (
            <article key={issue.id} className="listIssue">
              <span>{issue.key}</span>
              <strong>{issue.title}</strong>
              <select value="" onChange={(event) => moveIssue.mutate({ issueId: issue.id, sprintId: event.target.value })}>
                <option value="">Sprint</option>
                {sprints.data?.sprints.filter((sprint) => sprint.state !== "completed").map((sprint) => <option key={sprint.id} value={sprint.id}>{sprint.name}</option>)}
              </select>
            </article>
          ))}
        </div>
      </div>
      <div className="panel">
        <header className="panelHeader">
          <h2>Sprints</h2>
          <form className="inlineForm" onSubmit={(event) => { event.preventDefault(); createSprint.mutate(); }}>
            <input value={sprintName} onChange={(event) => setSprintName(event.target.value)} placeholder="Sprint name" />
            <button className="iconButton" title="Create sprint"><Plus size={16} /></button>
          </form>
        </header>
        <div className="sprintStack">
          {sprints.data?.sprints.map((sprint) => (
            <SprintPanel
              key={sprint.id}
              sprint={sprint}
              projectKey={projectKey}
              statuses={statuses}
              canStart={!sprints.data?.sprints.some((item) => item.state === "active") && sprint.state === "future"}
              onStart={() => sprintAction.mutate({ id: sprint.id, action: "start" })}
              onComplete={() => sprintAction.mutate({ id: sprint.id, action: "complete" })}
              onRemove={(issueId) => moveIssue.mutate({ issueId, sprintId: null })}
            />
          ))}
          {!futureSprint ? null : <span className="mutedText">Sprint capacity uses completed sprint velocity in reports.</span>}
        </div>
      </div>
    </section>
  );
}

function SprintPanel({ sprint, projectKey, canStart, onStart, onComplete, onRemove }: { sprint: Sprint; projectKey: string; statuses: WorkflowStatus[]; canStart: boolean; onStart: () => void; onComplete: () => void; onRemove: (issueId: string) => void }) {
  const issues = useQuery({ queryKey: ["issues", projectKey, sprint.id], queryFn: () => api.issues(projectKey, `?sprint=${sprint.id}&limit=200`) });
  const points = issues.data?.issues.reduce((sum, issue) => sum + (issue.storyPoints ?? 0), 0) ?? 0;
  return (
    <article className="sprintPanel">
      <header>
        <div><h3>{sprint.name}</h3><p>{sprint.state} · {points} points</p></div>
        <div className="buttonCluster">
          {canStart ? <button className="secondaryButton compact" onClick={onStart}>Start</button> : null}
          {sprint.state === "active" ? <button className="secondaryButton compact" onClick={onComplete}>Complete</button> : null}
        </div>
      </header>
      <div className="issueList tight">
        {issues.data?.issues.map((issue) => (
          <article key={issue.id} className="listIssue">
            <span>{issue.key}</span><strong>{issue.title}</strong>
            {sprint.state !== "completed" ? <button className="iconButton" title="Remove from sprint" onClick={() => onRemove(issue.id)}><ChevronLeft size={15} /></button> : null}
          </article>
        ))}
      </div>
    </article>
  );
}

function RoadmapView({ projectKey }: { projectKey: string }) {
  const roadmap = useQuery({ queryKey: ["roadmap", projectKey], queryFn: () => api.roadmap(projectKey) });
  const epics = roadmap.data?.epics ?? [];
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 5, 1);
  const total = end.getTime() - start.getTime();
  return (
    <section className="workSurface">
      <div className="surfaceToolbar"><h2>Roadmap</h2><span>{epics.length} timeline items</span></div>
      <div className="timeline">
        {epics.map((item) => {
          const itemStart = item.startDate ? new Date(String(item.startDate)) : start;
          const itemEnd = item.dueDate ? new Date(String(item.dueDate)) : new Date(itemStart.getTime() + 21 * 86400000);
          const left = Math.max(0, Math.min(95, ((itemStart.getTime() - start.getTime()) / total) * 100));
          const width = Math.max(8, Math.min(100 - left, ((itemEnd.getTime() - itemStart.getTime()) / total) * 100));
          return (
            <div className="timelineRow" key={String(item.id)}>
              <div className="timelineLabel"><strong>{String(item.key)}</strong><span>{String(item.title)}</span></div>
              <div className="timelineTrack"><div className="timelineBar" style={{ left: `${left}%`, width: `${width}%` }}><Rocket size={14} /> {String(item.status ?? "")}</div></div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ReportsView({ projectKey }: { projectKey: string }) {
  const velocity = useQuery({ queryKey: ["reports", projectKey, "velocity"], queryFn: () => api.report(projectKey, "velocity") });
  const burndown = useQuery({ queryKey: ["reports", projectKey, "burndown"], queryFn: () => api.report(projectKey, "burndown") });
  const cfd = useQuery({ queryKey: ["reports", projectKey, "cfd"], queryFn: () => api.report(projectKey, "cfd") });
  const cycle = useQuery({ queryKey: ["reports", projectKey, "cycle-time"], queryFn: () => api.report(projectKey, "cycle-time") });
  const cfdData = ((cfd.data?.data as Array<{ date: string; counts: Record<string, number> }> | undefined) ?? []).map((row) => ({ date: row.date, ...row.counts }));
  const cfdKeys = Object.keys(cfdData[0] ?? {}).filter((key) => key !== "date");
  return (
    <section className="reportGrid">
      <div className="chartPanel">
        <header><h2>Velocity</h2><span>{Number(velocity.data?.averageCompleted ?? 0)} avg</span></header>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={(velocity.data?.data as unknown[]) ?? []}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis /><Tooltip /><Legend /><Bar dataKey="committedPoints" fill="#64748b" /><Bar dataKey="completedPoints" fill="#10b981" /></BarChart>
        </ResponsiveContainer>
      </div>
      <div className="chartPanel">
        <header><h2>Burndown</h2></header>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={(burndown.data?.data as unknown[]) ?? []}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" /><YAxis /><Tooltip /><Legend /><Line type="monotone" dataKey="remaining" stroke="#ef4444" /><Line type="monotone" dataKey="ideal" stroke="#64748b" strokeDasharray="4 4" /></LineChart>
        </ResponsiveContainer>
      </div>
      <div className="chartPanel">
        <header><h2>Cumulative Flow</h2></header>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={cfdData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" /><YAxis /><Tooltip /><Legend />{cfdKeys.map((key, index) => <Area key={key} type="monotone" dataKey={key} stackId="1" fill={["#64748b", "#3b82f6", "#f59e0b", "#8b5cf6", "#10b981"][index % 5]} stroke="transparent" />)}</AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="chartPanel statsPanel">
        <header><h2>Cycle & Lead Time</h2></header>
        <Metric label="Lead p50" value={`${readNested(cycle.data, "leadHours", "p50")}h`} />
        <Metric label="Lead p95" value={`${readNested(cycle.data, "leadHours", "p95")}h`} />
        <Metric label="Cycle p50" value={`${readNested(cycle.data, "cycleHours", "p50")}h`} />
        <Metric label="Cycle p95" value={`${readNested(cycle.data, "cycleHours", "p95")}h`} />
      </div>
    </section>
  );
}

function SettingsView({ projectKey }: { projectKey: string }) {
  return (
    <Tabs.Root defaultValue="workflow" className="settingsTabs">
      <Tabs.List className="tabList">
        <Tabs.Trigger value="appearance"><SlidersHorizontal size={16} /> Appearance</Tabs.Trigger>
        <Tabs.Trigger value="workflow"><Workflow size={16} /> Workflow</Tabs.Trigger>
        <Tabs.Trigger value="fields"><FileText size={16} /> Fields</Tabs.Trigger>
        <Tabs.Trigger value="automation"><Activity size={16} /> Automation</Tabs.Trigger>
        <Tabs.Trigger value="webhooks"><Bell size={16} /> Webhooks</Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content value="appearance"><AppearanceSettings /></Tabs.Content>
      <Tabs.Content value="workflow"><WorkflowSettings projectKey={projectKey} /></Tabs.Content>
      <Tabs.Content value="fields"><FieldSettings projectKey={projectKey} /></Tabs.Content>
      <Tabs.Content value="automation"><AutomationSettings projectKey={projectKey} /></Tabs.Content>
      <Tabs.Content value="webhooks"><WebhookSettings projectKey={projectKey} /></Tabs.Content>
    </Tabs.Root>
  );
}

function WorkflowSettings({ projectKey }: { projectKey: string }) {
  const queryClient = useQueryClient();
  const workflow = useQuery({ queryKey: ["workflow", projectKey], queryFn: () => api.workflow(projectKey) });
  const [statuses, setStatuses] = useState<Array<Partial<WorkflowStatus>>>([]);
  useEffect(() => { if (workflow.data?.statuses) setStatuses(workflow.data.statuses); }, [workflow.data?.statuses]);
  const save = useMutation({
    mutationFn: () => api.saveWorkflowStatuses(projectKey, statuses.map((status, index) => ({ ...status, position: index }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow", projectKey] });
      queryClient.invalidateQueries({ queryKey: ["project", projectKey] });
    }
  });
  return (
    <div className="settingsPanel">
      <header><h2>Workflow Statuses</h2><button className="secondaryButton compact" onClick={() => setStatuses([...statuses, { name: "New Status", category: "todo", color: "#64748b", position: statuses.length }])}><Plus size={16} /> Status</button></header>
      <div className="statusEditor">
        {statuses.map((status, index) => (
          <div className="statusRow" key={`${status.id ?? "new"}-${index}`}>
            <input value={status.name ?? ""} onChange={(event) => setStatuses(replaceAt(statuses, index, { ...status, name: event.target.value }))} />
            <select value={status.category} onChange={(event) => setStatuses(replaceAt(statuses, index, { ...status, category: event.target.value as WorkflowStatus["category"] }))}><option value="todo">Todo</option><option value="in_progress">In progress</option><option value="done">Done</option></select>
            <input type="color" value={status.color ?? "#64748b"} onChange={(event) => setStatuses(replaceAt(statuses, index, { ...status, color: event.target.value }))} />
          </div>
        ))}
      </div>
      <button className="primaryButton compact" onClick={() => save.mutate()}>Save workflow</button>
    </div>
  );
}

function FieldSettings({ projectKey }: { projectKey: string }) {
  const queryClient = useQueryClient();
  const fields = useQuery({ queryKey: ["fields", projectKey], queryFn: () => api.fields(projectKey) });
  const [form, setForm] = useState<Partial<CustomFieldDefinition>>({ name: "", fieldKey: "", type: "short_text", options: [], requiredForTypes: [] });
  const createField = useMutation({
    mutationFn: () => api.createField(projectKey, form),
    onSuccess: () => {
      setForm({ name: "", fieldKey: "", type: "short_text", options: [], requiredForTypes: [] });
      queryClient.invalidateQueries({ queryKey: ["fields", projectKey] });
    }
  });
  return (
    <div className="settingsPanel">
      <header><h2>Custom Fields</h2></header>
      <form className="formGrid" onSubmit={(event) => { event.preventDefault(); createField.mutate(); }}>
        <label>Name<input value={form.name ?? ""} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
        <label>Key<input value={form.fieldKey ?? ""} onChange={(event) => setForm({ ...form, fieldKey: event.target.value })} /></label>
        <label>Type<select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value as CustomFieldDefinition["type"] })}><option value="short_text">Short text</option><option value="long_text">Long text</option><option value="number">Number</option><option value="date">Date</option><option value="single_select">Single select</option><option value="multi_select">Multi select</option><option value="user">User</option><option value="url">URL</option><option value="checkbox">Checkbox</option></select></label>
        <button className="primaryButton">Add field</button>
      </form>
      <div className="dataTable">
        {fields.data?.fields.map((field) => <div key={field.id}><strong>{field.name}</strong><span>{field.fieldKey}</span><span>{field.type}</span></div>)}
      </div>
    </div>
  );
}

function AutomationSettings({ projectKey }: { projectKey: string }) {
  const queryClient = useQueryClient();
  const automations = useQuery({ queryKey: ["automations", projectKey], queryFn: () => api.automations(projectKey) });
  const [form, setForm] = useState({
    name: "Auto comment on review",
    triggerType: "issue.transitioned",
    toStatus: "Review",
    conditionField: "issue.type",
    conditionValue: "bug",
    actionType: "add_comment",
    actionMessage: "Bug {{issue.key}} needs review."
  });
  const createAutomation = useMutation({
    mutationFn: () =>
      api.createAutomation(projectKey, {
        name: form.name,
        active: true,
        trigger: { type: form.triggerType, to_status: form.toStatus },
        conditions: form.conditionField ? [{ field: form.conditionField, operator: "equals", value: form.conditionValue }] : [],
        actions: [{ type: form.actionType, message: form.actionMessage, comment: form.actionMessage }]
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["automations", projectKey] })
  });
  return (
    <div className="settingsPanel">
      <header><h2>Automation Rules</h2></header>
      <form className="wizardGrid" onSubmit={(event) => { event.preventDefault(); createAutomation.mutate(); }}>
        <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
        <label>Trigger<select value={form.triggerType} onChange={(event) => setForm({ ...form, triggerType: event.target.value })}><option value="issue.created">Issue created</option><option value="issue.updated">Issue updated</option><option value="issue.transitioned">Issue transitioned</option><option value="comment.created">Comment created</option></select></label>
        <label>To status<input value={form.toStatus} onChange={(event) => setForm({ ...form, toStatus: event.target.value })} /></label>
        <label>Condition field<input value={form.conditionField} onChange={(event) => setForm({ ...form, conditionField: event.target.value })} /></label>
        <label>Condition value<input value={form.conditionValue} onChange={(event) => setForm({ ...form, conditionValue: event.target.value })} /></label>
        <label>Action<select value={form.actionType} onChange={(event) => setForm({ ...form, actionType: event.target.value })}><option value="add_comment">Add comment</option><option value="set_priority">Set priority</option><option value="add_label">Add label</option><option value="notify">Notify</option></select></label>
        <label className="span2">Message<textarea value={form.actionMessage} onChange={(event) => setForm({ ...form, actionMessage: event.target.value })} /></label>
        <button className="primaryButton">Create rule</button>
      </form>
      <div className="dataTable">
        {automations.data?.automations.map((rule: AutomationRule) => <div key={rule.id}><strong>{rule.name}</strong><span>{rule.active ? "active" : "paused"}</span><span>{String(rule.trigger.type ?? "")}</span></div>)}
      </div>
    </div>
  );
}

function WebhookSettings({ projectKey }: { projectKey: string }) {
  const queryClient = useQueryClient();
  const webhooks = useQuery({ queryKey: ["webhooks", projectKey], queryFn: () => api.webhooks(projectKey) });
  const [form, setForm] = useState({ name: "", url: "", secret: "", events: "issue.created,issue.updated,issue.transitioned" });
  const createWebhook = useMutation({
    mutationFn: () => api.createWebhook(projectKey, { name: form.name, url: form.url, secret: form.secret || null, events: parseLabels(form.events), active: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["webhooks", projectKey] })
  });
  return (
    <div className="settingsPanel">
      <header><h2>Outgoing Webhooks</h2></header>
      <form className="formGrid" onSubmit={(event) => { event.preventDefault(); createWebhook.mutate(); }}>
        <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
        <label>URL<input value={form.url} onChange={(event) => setForm({ ...form, url: event.target.value })} /></label>
        <label>Secret<input value={form.secret} onChange={(event) => setForm({ ...form, secret: event.target.value })} /></label>
        <label>Events<input value={form.events} onChange={(event) => setForm({ ...form, events: event.target.value })} /></label>
        <button className="primaryButton">Add webhook</button>
      </form>
      <div className="dataTable">
        {webhooks.data?.webhooks.map((hook) => <div key={String(hook.id)}><strong>{String(hook.name)}</strong><span>{String(hook.url)}</span><span>{String(hook.active)}</span></div>)}
      </div>
    </div>
  );
}

function ThemeMenu() {
  const theme = useUiStore((state) => state.theme);
  const setTheme = useUiStore((state) => state.setTheme);
  const density = useUiStore((state) => state.density);
  const setDensity = useUiStore((state) => state.setDensity);
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <button className="iconButton themeButton" title="Appearance">
          <Palette size={18} />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="dialogOverlay" />
        <Dialog.Content className="dialogCard appearancePopover">
          <Dialog.Title>Appearance</Dialog.Title>
          <div className="themeGrid">
            {themeOptions.map((option) => (
              <button
                key={option.id}
                className={clsx("themeChoice", theme === option.id && "selected")}
                onClick={() => setTheme(option.id)}
                type="button"
              >
                <span className="themeSwatch" style={{ background: option.accent }} />
                <span>{option.name}</span>
              </button>
            ))}
          </div>
          <div className="segmentedControl">
            <button className={clsx(density === "comfortable" && "selected")} onClick={() => setDensity("comfortable")} type="button">
              Comfortable
            </button>
            <button className={clsx(density === "compact" && "selected")} onClick={() => setDensity("compact")} type="button">
              Compact
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function AppearanceSettings() {
  const theme = useUiStore((state) => state.theme);
  const setTheme = useUiStore((state) => state.setTheme);
  const density = useUiStore((state) => state.density);
  const setDensity = useUiStore((state) => state.setDensity);
  const sidebarStyle = useUiStore((state) => state.sidebarStyle);
  const setSidebarStyle = useUiStore((state) => state.setSidebarStyle);
  return (
    <div className="settingsPanel appearanceSettings">
      <header><h2>Appearance</h2></header>
      <section className="themeShowcase">
        {themeOptions.map((option) => (
          <button
            key={option.id}
            className={clsx("themePreview", theme === option.id && "selected")}
            onClick={() => setTheme(option.id)}
            type="button"
          >
            <span className="previewChrome" style={{ background: option.accent }} />
            <span className="previewCard" />
            <strong>{option.name}</strong>
          </button>
        ))}
      </section>
      <section className="preferenceGrid">
        <div>
          <h3>Density</h3>
          <div className="segmentedControl">
            <button className={clsx(density === "comfortable" && "selected")} onClick={() => setDensity("comfortable")} type="button">
              Comfortable
            </button>
            <button className={clsx(density === "compact" && "selected")} onClick={() => setDensity("compact")} type="button">
              Compact
            </button>
          </div>
        </div>
        <div>
          <h3>Sidebar</h3>
          <div className="segmentedControl">
            <button className={clsx(sidebarStyle === "solid" && "selected")} onClick={() => setSidebarStyle("solid")} type="button">
              Solid
            </button>
            <button className={clsx(sidebarStyle === "glass" && "selected")} onClick={() => setSidebarStyle("glass")} type="button">
              Glass
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="metric"><span>{label}</span><strong>{value}</strong></div>;
}

function InlineStatus({ label }: { label: string }) {
  return <div className="inlineStatus">{label}</div>;
}

function FullScreenStatus({ label }: { label: string }) {
  return <main className="fullStatus"><KanbanSquare size={28} /><span>{label}</span></main>;
}

function parseLabels(value: string): string[] {
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

function rankNow(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function readNested(value: unknown, key: string, inner: string): number {
  const record = value as Record<string, Record<string, number>> | undefined;
  return record?.[key]?.[inner] ?? 0;
}

function replaceAt<T>(items: T[], index: number, value: T): T[] {
  return items.map((item, current) => (current === index ? value : item));
}

function readUiPreference<T extends string>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  return (localStorage.getItem(key) as T | null) ?? fallback;
}
